import api from '../utils/api';

/**
 * Package administration service
 * Mirrors backend routes under /api/packages
 */
export const packageService = {
  /**
   * Get paginated list of packages for admin
   * @param {Object} params
   */
  getAdminPackages: async (params = {}) => {
    return api.get('/api/packages/admin/all', { params });
  },

  /**
   * Get public package details (includes populated courses)
   * @param {string} id
   */
  getPackageById: async (id) => {
    return api.get(`/api/packages/${id}`);
  },

  /**
   * Create new package
   * @param {Object} payload
   */
  createPackage: async (payload) => {
    return api.post('/api/packages', payload);
  },

  /**
   * Update package by id
   * @param {string} id
   * @param {Object} updates
   */
  updatePackage: async (id, updates) => {
    return api.put(`/api/packages/${id}`, updates);
  },

  /**
   * Soft delete (deactivate) package
   * @param {string} id
   */
  deletePackage: async (id) => {
    return api.delete(`/api/packages/${id}`);
  },

  /**
   * Reorder packages (admin)
   * @param {Array<{id: string, orderIndex: number}>} packages
   * @returns {Promise<{status: string, message: string, data: {updatedCount: number}}>}
   */
  reorderPackages: async (packages) => {
    return api.patch('/api/packages/reorder', { packages });
  },

  /**
   * Fetch package statistics snapshot
   * @param {string} id
   */
  getPackageStats: async (id) => {
    return api.get(`/api/packages/${id}/stats`);
  },

  /**
   * Grant a package to a student (admin override, no payment)
   * Calls POST /api/packages/admin/grant
   * @param {string} userId - The student ID
   * @param {string} packageId - The package ID
   * @param {string} [enrollmentExpiresAt] - Optional ISO date string to override the package's default validity
   * @param {string} [couponCode] - Optional package coupon code to discount this grant
   * @returns {Promise<{status: string, message: string, data: {packageEnrollment: object, enrollments: object[]}}>}
   */
  grantPackageToUser: async (userId, packageId, enrollmentExpiresAt, couponCode) => {
    const payload = { userId, packageId };
    if (enrollmentExpiresAt) {
      payload.enrollmentExpiresAt = enrollmentExpiresAt;
    }
    if (couponCode) {
      payload.couponCode = couponCode;
    }
    return api.post('/api/packages/admin/grant', payload);
  },
};
